<script setup lang="ts">
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Select from 'primevue/select';
import Button from 'primevue/button';
import { computed, onMounted, ref } from 'vue';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import Message from 'primevue/message';
import ProblemDetailsView from 'shared/problems/components/ProblemDetailsView.vue';
import type { ProblemDetails } from "shared/problems/domain/problemDetails";
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import { useCreateCoil, type CreateCoilRequest } from '../api/createCoil';
import { useUpdateCoil, type UpdateCoilRequest } from '../api/updateCoil';
import { Assert } from 'shared/assert';
import { StorageAreaType } from 'shared/storageAreas/domain/storageAreaType';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import CoilStackPositionSelect, { type CoilModel } from 'shared/coils/components/CoilStackPositionSelect.vue';
import { useStorageAreaToCoilStack } from '../domain/coilStackPositionSelect';
import { useProductStore } from 'shared/products/stores/productStore';
import type { Product } from 'shared/products/domain/product';
import type { StorageArea } from 'shared/storageAreas/domain/storageArea';

defineExpose({
  show
});

const coilId = ref<string>();

const createCoil = useCreateCoil();
const updateCoil = useUpdateCoil();
const storageAreaToCoilStack = useStorageAreaToCoilStack();

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);

const storageAreaStore = useStorageAreaStore();
const coilStorageAreas = computed(() => storageAreaStore.storageAreas
  .filter(s => s.type === StorageAreaType.CoilStack)
  .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })));

const productStore = useProductStore();
const { products } = storeToRefs(productStore);

const coilProducts = computed(() => products.value.filter(p => p.coilData));

const dialogTranslations = translations.value.coils.editDialog;

const visible = ref<boolean>();

const loading = ref(false);
const problems = ref<ProblemDetails>();

const initialValues = ref<ShowDialog>();

const title = computed(() => {
  if (coilId.value) {
    return dialogTranslations.edit.title;
  } else {
    return dialogTranslations.create.title;
  }
});

const name = ref('');

const nameChanged = computed(() => {
  if (!coilId.value) return false;
  if (initialValues.value === undefined) return false;
  return initialValues.value.name !== name.value;
});

const nameInvalid = computed(() => {
  if (name.value === '') {
    return translations.value.error.nameIsRequired;
  }

  return undefined;
});

const diameter = ref<number>();

const diameterChanged = computed(() => {
  if (!coilId.value) return false;
  if (initialValues.value === undefined) return false;
  return initialValues.value.diameter !== diameter.value;
});

const diameterInvalid = computed(() => {
  if (!diameter.value) {
    return translations.value.error.required;
  }

  return undefined;
});

const depth = ref<number>();

const depthChanged = computed(() => {
  if (!coilId.value) return false;
  if (initialValues.value === undefined) return false;
  return initialValues.value.depth !== depth.value;
});

const selectedProduct = ref<Product>();

const productChanged = computed(() => {
  if (!coilId.value) return false;
  if (initialValues.value === undefined) return false;
  return initialValues.value.productId !== selectedProduct.value?.id;
});

const productInvalid = computed(() => {
  if (!selectedProduct.value) {
    return translations.value.error.required;
  }

  return undefined;
});

const selectedStorageArea = ref<StorageArea>();

const selectedCoilStack = computed(() => {
  const storageArea = selectedStorageArea.value;
  if (!storageArea || !storageArea.coilStack) return;
  return storageAreaToCoilStack.transform(storageArea);
});

const selectedCoil = ref<CoilModel>();

const placeCoil = computed<CoilModel|undefined>(() => {
  const diameterValue = diameter.value;

  if (diameterValue === undefined) return;

  const coil: CoilModel = {
    diameter: diameterValue,
    level: -1,
    id: coilId.value ?? '',
    position: -1,
    name: ''
  };

  return coil;
});

const storageAreaChanged = computed(() => {
  if (!coilId.value) return false;
  if (initialValues.value === undefined) return false;
  return selectedStorageArea.value?.id !== initialValues.value.storageAreaId;
});

const level = computed(() => selectedCoil.value?.level ?? 0);
const levelChanged = computed(() => {
  if (!coilId.value) return false;
  if (initialValues.value === undefined) return false;
  if (!selectedCoil.value) return false;
  return level.value !== initialValues.value.level;
});

const position = computed(() => selectedCoil.value?.position ?? 0);
const positionChanged = computed(() => {
  if (!coilId.value) return false;
  if (initialValues.value === undefined) return false;
  if (!selectedCoil.value) return false;
  return position.value !== initialValues.value.position;
});

const changed = computed(() => nameChanged.value ||
  diameterChanged.value ||
  productChanged.value ||
  storageAreaChanged.value ||
  levelChanged.value ||
  positionChanged.value);

const invalid = computed(() => nameInvalid.value ||
  diameterInvalid.value ||
  productInvalid.value
);

const saveDisabled = computed(() => {
  if (invalid.value || (coilId.value && !changed.value)) {
    return true;
  }
  return false;
});

onMounted(() => reset());

export interface ShowDialog {
  id?: string;
  name?: string | null;
  diameter?: number | null;
  depth?: number | null;
  productId?: string | null;
  storageAreaId?: string | null;
  level?: number | null;
  position?: number | null;
}

async function show(options: ShowDialog) {
  try {
    reset();
    coilId.value = options.id;

    name.value = options.name ?? '';
    diameter.value = options.diameter ?? undefined;
    depth.value = options.depth ?? undefined;

    if (options.productId) {
      selectedProduct.value = productStore.getById(options.productId);
    }

    if (options.storageAreaId) {
      selectedStorageArea.value = storageAreaStore.getById(options.storageAreaId);
    }

    selectedCoil.value = undefined;

    initialValues.value = options;
    visible.value = true;

  } finally {
    loading.value = false;
  }
}

function reset() {
  loading.value = false;
  problems.value = undefined;
  coilId.value = undefined;
  name.value = '';
  diameter.value = undefined;
  depth.value = undefined;
  selectedProduct.value = undefined;
  selectedStorageArea.value = undefined;
  selectedCoil.value = undefined;
}

async function onSave() {

  try {
    loading.value = true;

    const id = coilId.value;

    if (id === undefined) {
      await create();
    } else {
      await update(id);
    }

  } finally {
    loading.value = false;
  }
}

async function create() {

  const diameterValue = diameter.value;
  Assert.definied(diameterValue);

  const selectedProductValue = selectedProduct.value;
  Assert.definied(selectedProductValue);

  const selectedStorageAreaValue = selectedStorageArea.value;
  Assert.definied(selectedStorageAreaValue);

  const levelValue = level.value;
  Assert.definied(levelValue);

  const positionValue = position.value;
  Assert.definied(positionValue);

  const request: CreateCoilRequest = {
      coil: {
        name: name.value,

        diameter: diameterValue,
        depth: depth.value ?? null,
        productId: selectedProductValue.id,

        storageAreaId: selectedStorageAreaValue.id,
        level: levelValue,
        position: positionValue
      }
    };

    const response = await createCoil.execute(request);

    if (!response.ok) {
      problems.value = response.details;
      return;
    }

    visible.value = false;
}

async function update(id: string) {

  const request: UpdateCoilRequest = {
      coilId: id,
      coil: {
        name: nameChanged.value ? name.value : undefined,
        depth: depthChanged.value ? depth.value : undefined,
        diameter: diameterChanged.value ? diameter.value : undefined,
        productId: productChanged.value ? selectedProduct.value?.id : undefined,
        storageAreaId: storageAreaChanged.value ? selectedStorageArea.value?.id : undefined,
        level: levelChanged.value ? level.value : undefined,
        position: positionChanged.value ? position.value : undefined
      }
    };

    const response = await updateCoil.execute(request);

    if (!response.ok) {
      problems.value = response.details;
      return;
    }

    coilId.value = undefined;
    visible.value = false;
}

function onHide() {
  coilId.value = undefined;
}

function onProductChanged() {
  const product = selectedProduct.value;
  Assert.definied(product);

  const coilData = product.coilData;
  Assert.definied(coilData);

  if (diameter.value === undefined) {
    diameter.value = coilData.diameter ?? undefined;
  }

  if (depth.value === undefined) {
    depth.value = coilData.depth ?? undefined;
  }
}

</script>

<template>
  <Dialog class="edit-coil-dialog" modal ref="dialog" v-model:visible="visible" :header="title" @hide="onHide" :closeOnEscape="false">
    <ProblemDetailsView v-model="problems"></ProblemDetailsView>
    <div class="coil-form">

      <div class="field" :class="{ changed: nameChanged }">
        <label for="edit-coil-dialog-name">{{ translations.generic.name }}</label>
        <InputText autocomplete="off" v-model:modelValue="name" autofocus id="edit-coil-dialog-name"></InputText>
        <Message v-if="nameInvalid" severity="error" variant="simple">{{ nameInvalid }}</Message>
      </div>

      <div class="field" :class="{ changed: productChanged }">
        <label for="edit-coil-dialog-product">{{ translations.coils.product }}</label>
        <Select @change="onProductChanged" autocomplete="off" v-model:modelValue="selectedProduct" :options="coilProducts" option-label="name" autofocus id="edit-coil-dialog-product"></Select>
        <Message v-if="nameInvalid" severity="error" variant="simple">{{ productInvalid }}</Message>
      </div>

      <div class="field" :class="{ changed: diameterChanged }">
        <label for="edit-coil-dialog-diameter">{{ translations.coils.diameter }}</label>
        <InputGroup>
          <InputNumber autocomplete="off" v-model:modelValue="diameter" autofocus id="edit-coil-dialog-diameter"></InputNumber>
          <InputGroupAddon>{{ translations.units.millimeters }}</InputGroupAddon>
        </InputGroup>

        <Message v-if="diameterInvalid" severity="error" variant="simple">{{ diameterInvalid }}</Message>
      </div>

      <div class="field" :class="{ changed: depthChanged }">
        <label for="edit-coil-dialog-depth">{{ translations.coils.depth }}</label>
        <InputGroup>
          <InputNumber autocomplete="off" v-model:modelValue="depth" autofocus id="edit-coil-dialog-depth"></InputNumber>
          <InputGroupAddon>{{ translations.units.millimeters }}</InputGroupAddon>
        </InputGroup>
      </div>

      <div class="field" :class="{ changed: storageAreaChanged }">
        <label for="edit-coil-dialog-storage-area">{{ translations.coils.storageArea }}</label>
        <Select autocomplete="off" v-model:modelValue="selectedStorageArea"
          :options="coilStorageAreas" option-label="name" autofocus
          id="edit-coil-dialog-storage-area"></Select>
      </div>

      <CoilStackPositionSelect v-if="selectedCoilStack"
        v-model:model-value="selectedCoilStack"
        mode="place"
        v-model:selected-coil="selectedCoil"
        :place-coil="placeCoil"
        :same-storage-area="true">
      </CoilStackPositionSelect>

      <Button class="submit-button"
        :disabled="saveDisabled"
        :label="translations.generic.save" @click="onSave" :loading="loading"></Button>
    </div>
  </Dialog>
</template>

<style scoped>

.coil-form {
  display: grid;
  row-gap: .5rem;
  column-gap: .5rem;
  grid-template-columns: 1fr;
  min-width: 30rem;
}

.description-field {
  grid-column: 1/4;
}

.field {
  display: flex;
  flex-direction: column;
}

.submit-button {
  justify-self: end;
}

.log-fields {
  grid-column: 1/4;
}

.log-fields :deep(.p-fieldset-content) {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  row-gap: .5rem;
  column-gap: .5rem;
}

.field :deep(.p-inputnumber-input) {
  text-align: right;
}

.toggle-field {
  display: flex;
  align-items: center;
  column-gap: .5rem;
}

</style>

<style>

.edit-coil-dialog .p-dialog-content{
  overflow-x: hidden;
}

.edit-coil-dialog .p-fieldset {
  display: grid;
}

.edit-coil-dialog .p-fieldset-content-container {
  overflow-x: hidden;
}


</style>
